<template>
  <div class="my-agents">
    <!-- Tab 切换栏 -->
    <div class="tab-container">
      <div class="tab-bar">
        <div v-for="(tab, idx) in tabs" :key="tab" :class="['tab', { active: activeTab === idx }]"
          @click="activeTab = idx">
          <span class="tab-text">{{ tab }}</span>
        </div>
      </div>
    </div>

    <!-- Tab 内容区 -->
    <div class="tab-content">
      <!-- 我的智能体 Tab -->
      <div v-if="activeTab === 0" class="my-agents-tab">
        <!-- 头部操作区 -->
        <div class="header-section">
          <div class="title-area">
            <h2>我的智能体</h2>
            <p>创建和管理您的专属智能体</p>
          </div>
          <div class="action-area">
            <el-button type="primary" icon="el-icon-plus" @click="showCreateDialog">
              创建智能体
            </el-button>
          </div>
        </div>

        <!-- 智能体列表 -->
        <div class="agents-content" v-loading="loading">
          <div v-if="myAgentsList.length === 0 && !loading" class="empty-state">
            <div class="empty-content">
              <i class="el-icon-cpu"></i>
              <h3>还没有创建智能体</h3>
              <p>创建您的第一个智能体，开始AI创作之旅</p>
              <el-button type="primary" @click="showCreateDialog">立即创建</el-button>
            </div>
          </div>

          <div v-else class="agents-grid">
            <!-- 使用小程序风格的DOM结构 -->
            <div class="agent-card" v-for="(agent, index) in myAgentsList" :key="index">
              <img class="agent-avatar" :src="agent.avatar || defaultAvatar" />
              <div class="agent-info" @click="handleAgentClick(agent)">
                <div class="agent-title">{{ agent.name }}</div>
                <div class="agent-desc">{{ agent.description }}</div>
                <div class="agent-tags">
                  <div class="tag primary">
                    {{agentTypes.find(item => item.value === agent.agentType)?.label || '未知类型'}}
                  </div>
                  <div class="tag primary">
                    {{ agent.priceText }}
                  </div>
                  <div class="tag primary">
                    {{ agent.isPublicText }}
                  </div>
                </div>
              </div>
              <div class="operate-box">
                <div class="edit" @click="handleEditAgent(agent)">编辑</div>
                <div class="delete" @click="handleDeleteAgent(agent)">删除</div>
              </div>
              <div class="status" v-if="agent.auditStatus != 2">{{ auditStatus[agent.auditStatus] }}</div>
              <div class="status success" v-if="agent.auditStatus === 2">{{ auditStatus[agent.auditStatus] }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 共创收益 Tab -->
      <div v-else-if="activeTab === 1" class="income-tab">
        <div class="income-content">
          <!-- 共创规则 -->
          <div class="rules-section">
            <h3 class="section-title">共创规则</h3>
            <div class="rules-grid">
              <div v-for="(rule, index) in rulesList" :key="index" class="rule-item" @click="handleRuleClick(index)">
                <span class="rule-text">{{ rule.name }}</span>
              </div>
            </div>
          </div>

          <!-- 我的收益 -->
          <div class="my-income-section">
            <h3 class="section-title">我的收益</h3>
            <div class="income-card">
              <div class="income-amount">
                <span class="amount-value">{{ incomeData.availableAmountText }}</span>
                <span class="amount-unit">可用余额(元)</span>
              </div>
              <el-button class="withdraw-btn" type="primary" @click="handleWithdraw">
                提现
              </el-button>
            </div>
          </div>

          <!-- 收益总览 -->
          <div class="overview-section">
            <h3 class="section-title">收益总览</h3>
            <div class="overview-grid">
              <div class="overview-item">
                <span class="overview-value">{{ incomeData.totalEarningsText }}</span>
                <span class="overview-label">总收益(元)</span>
              </div>
              <div class="overview-item">
                <span class="overview-value">{{ incomeData.totalWithdrawnText }}</span>
                <span class="overview-label">总提现(元)</span>
              </div>
            </div>
          </div>

          <!-- 收益流水 -->
          <div class="flow-section">
            <h3 class="section-title">收益流水</h3>
            <div class="flow-buttons">
              <el-button class="flow-btn" @click="handleWithdrawRecord">
                <i class="el-icon-document"></i>
                提现记录
              </el-button>
              <el-button class="flow-btn" @click="handleFinanceFlow">
                <i class="el-icon-money"></i>
                财务流水
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- 会员列表 Tab -->
      <div v-else-if="activeTab === 2" class="vip-list-tab">
        <div class="vip-content">
          <!-- 会员卡片 -->
          <div class="vip-card">
            <div class="vip-info">
              <h3 class="vip-title" v-if="!vipInfo.hasMembership">{{ shareText.noMemberTip }}</h3>
              <h3 class="vip-title" v-else>{{ shareText.hasMemberTip }}({{ vipInfo.remainingDays }}天)</h3>
              <p class="vip-desc" v-if="!vipInfo.hasMembership">{{ shareText.noMemberSlogan }}</p>
              <p class="vip-desc" v-else>{{ shareText.hasMemberSlogan }}</p>
            </div>
            <div class="vip-points">
              <span class="points-label">AI点数:</span>
              <span class="points-value">{{ vipInfo.userPoints.totalPointsText }}</span>
            </div>
          </div>

          <!-- 会员套餐 -->
          <div class="vip-plans">
            <h3 class="section-title">会员套餐</h3>
            <div class="plan-grid">
              <div v-for="(plan, index) in vipPlans" :key="index"
                :class="['plan-card', { 'selected': selectedPlan === index }]" @click="selectPlan(index)">
                <div class="plan-header">
                  <span class="plan-name">{{ plan.packageName }}</span>
                </div>
                <div class="plan-price">
                  <span class="price-symbol">¥</span>
                  <span class="price-amount">{{ plan.salePriceYuan }}</span>
                </div>
                <div class="plan-duration">{{ plan.durationDays }}天</div>
                <div class="plan-desc">{{ plan.packageDesc }}</div>
              </div>
            </div>
            <el-button class="subscribe-btn" type="primary" @click="handleSubscribe" :loading="subscribeLoading">
              立即订阅
            </el-button>
          </div>
        </div>
      </div>

      <!-- 财务流水 Tab -->
      <div v-else-if="activeTab === 3" class="finance-record-tab">
        <div class="finance-record-page">
          <!-- 统计数据卡片 -->
          <div class="summary-section" v-if="summaryData">
            <el-row :gutter="20">
              <el-col :span="6">
                <el-card class="summary-card">
                  <div class="summary-item">
                    <div class="summary-label">总收入</div>
                    <div class="summary-value income">+¥{{ summaryData.totalIncome }}</div>
                  </div>
                </el-card>
              </el-col>
              <el-col :span="6">
                <el-card class="summary-card">
                  <div class="summary-item">
                    <div class="summary-label">总支出</div>
                    <div class="summary-value expense">-¥{{ summaryData.totalExpense }}</div>
                  </div>
                </el-card>
              </el-col>
              <el-col :span="6">
                <el-card class="summary-card">
                  <div class="summary-item">
                    <div class="summary-label">佣金收入</div>
                    <div class="summary-value income">+¥{{ summaryData.commissionIncome }}</div>
                  </div>
                </el-card>
              </el-col>
              <el-col :span="6">
                <el-card class="summary-card">
                  <div class="summary-item">
                    <div class="summary-label">提现支出</div>
                    <div class="summary-value expense">-¥{{ summaryData.withdrawExpense }}</div>
                  </div>
                </el-card>
              </el-col>
            </el-row>
          </div>

          <!-- 财务流水列表 -->
          <div class="record-section">
            <el-card>
              <div slot="header" class="clearfix">
                <span class="section-title">财务流水记录</span>
                <el-button style="float: right; padding: 3px 0" type="text" @click="refreshFinanceData"
                  :loading="financeLoading">
                  刷新
                </el-button>
              </div>

              <div class="record-list" v-loading="financeLoading">
                <div class="record-item" v-for="item in financeRecordList" :key="item.guid">
                  <div class="record-content">
                    <div class="record-header">
                      <div class="transaction-info">
                        <div class="transaction-type">
                          <el-tag :type="getBusinessTagType(item.businessType)" size="small">
                            {{ item.businessTypeText }}
                          </el-tag>
                          <el-tag :type="item.type === 'income' ? 'success' : 'warning'" size="mini"
                            style="margin-left: 8px;">
                            {{ item.flowTypeText }}
                          </el-tag>
                        </div>
                        <div class="transaction-desc">{{ item.description }}</div>
                      </div>
                      <div class="amount-info">
                        <div class="amount" :class="item.type === 'income' ? 'income' : 'expense'">
                          {{ item.amountText }}
                        </div>
                        <div class="business-type" v-if="item.businessType">
                          业务类型：{{ item.businessTypeText }}
                        </div>
                      </div>
                    </div>
                    <div class="record-footer">
                      <div class="time-info">{{ item.createTime }}</div>
                      <div class="order-info" v-if="item.orderNo">
                        <span>订单号：{{ item.orderNo }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 空状态 -->
                <div v-if="financeRecordList.length === 0 && !financeLoading" class="empty-state">
                  <i class="el-icon-document"></i>
                  <p>暂无财务流水记录</p>
                </div>

                <!-- 分页 -->
                <div v-if="financePagination.total > 0" class="pagination-wrapper">
                  <el-pagination @current-change="handleFinancePageChange"
                    :current-page="financePagination.current_page" :page-size="financePagination.per_page"
                    :total="financePagination.total" layout="prev, pager, next, jumper">
                  </el-pagination>
                </div>
              </div>
            </el-card>
          </div>
        </div>
      </div>

      <!-- 提现记录 Tab -->
      <div v-else class="withdrawal-record-tab">
        <div class="withdrawal-record-page">
          <!-- 提现记录列表 -->
          <div class="record-section">
            <el-card>
              <div slot="header" class="clearfix">
                <span class="section-title">提现记录</span>
                <el-button style="float: right; padding: 3px 0" type="text" @click="refreshWithdrawalData"
                  :loading="withdrawalLoading">
                  刷新
                </el-button>
              </div>

              <div class="record-list" v-loading="withdrawalLoading">
                <div class="record-item" v-for="item in withdrawalRecordList" :key="item.guid">
                  <div class="record-content">
                    <div class="record-header">
                      <div class="withdrawal-info">
                        <div class="order-info">
                          <span class="order-label">提现单号：</span>
                          <span class="order-value">{{ item.withdrawOrderNo }}</span>
                          <el-button type="text" size="mini" @click="copyOrderNo(item.withdrawOrderNo)">
                            复制
                          </el-button>
                        </div>
                        <div class="status-info">
                          <el-tag :type="getStatusTagType(item.status)" size="small">
                            {{ item.statusText }}
                          </el-tag>
                        </div>
                      </div>
                      <div class="amount-info">
                        <div class="amount">{{ item.amountText }}</div>
                        <div class="actual-amount" v-if="item.actualAmount !== item.amount">
                          实际到账：¥{{ item.actualAmount }}
                        </div>
                      </div>
                    </div>

                    <div class="record-body">
                      <el-row :gutter="20">
                        <el-col :span="6">
                          <div class="info-item">
                            <span class="label">提现方式：</span>
                            <span class="value">{{ item.withdrawalMethodText }}</span>
                          </div>
                        </el-col>
                        <el-col :span="6">
                          <div class="info-item">
                            <span class="label">收款账号：</span>
                            <span class="value">{{ item.accountInfo }}</span>
                          </div>
                        </el-col>
                        <el-col :span="6">
                          <div class="info-item">
                            <span class="label">收款人：</span>
                            <span class="value">{{ item.withdrawName }}</span>
                          </div>
                        </el-col>
                        <el-col :span="6">
                          <div class="info-item">
                            <span class="label">申请时间：</span>
                            <span class="value">{{ item.createTime }}</span>
                          </div>
                        </el-col>
                      </el-row>
                      <el-row :gutter="20" v-if="item.processTime || item.withdrawFee || item.remark">
                        <el-col :span="6" v-if="item.processTime">
                          <div class="info-item">
                            <span class="label">处理时间：</span>
                            <span class="value">{{ item.processTime }}</span>
                          </div>
                        </el-col>
                        <el-col :span="6" v-if="item.withdrawFee && parseFloat(item.withdrawFee) > 0">
                          <div class="info-item">
                            <span class="label">手续费：</span>
                            <span class="value">¥{{ item.withdrawFee }}</span>
                          </div>
                        </el-col>
                        <el-col :span="12" v-if="item.remark">
                          <div class="info-item">
                            <span class="label">备注：</span>
                            <span class="value">{{ item.remark }}</span>
                          </div>
                        </el-col>
                      </el-row>
                    </div>
                  </div>
                </div>

                <!-- 空状态 -->
                <div v-if="withdrawalRecordList.length === 0 && !withdrawalLoading" class="empty-state">
                  <i class="el-icon-wallet"></i>
                  <p>暂无提现记录</p>
                </div>

                <!-- 分页 -->
                <div v-if="withdrawalPagination.total > 0" class="pagination-wrapper">
                  <el-pagination @current-change="handleWithdrawalPageChange"
                    :current-page="withdrawalPagination.current_page" :page-size="withdrawalPagination.per_page"
                    :total="withdrawalPagination.total" layout="prev, pager, next, jumper">
                  </el-pagination>
                </div>
              </div>
            </el-card>
          </div>
        </div>
      </div>
    </div>

    <!-- 小程序码弹窗 -->
    <el-dialog title="小程序码" :visible.sync="showMiniCodeModal" width="400px" center>
      <div class="mini-code-content">
        <img v-if="miniCodeUrl" :src="miniCodeUrl" class="mini-code-image" alt="小程序码" />
        <p class="mini-code-tip">扫描二维码进入小程序</p>
      </div>
    </el-dialog>

    <!-- 规则弹窗 -->
    <el-dialog :title="currentRuleTitle" :visible.sync="showRuleModal" width="600px">
      <div class="rule-content">
        <p class="rule-text">{{ currentRuleContent }}</p>
      </div>
    </el-dialog>

    <!-- 创建/编辑智能体对话框 -->
    <el-dialog :title="editingAgent ? '编辑智能体' : '创建智能体'" :visible.sync="showDialog" width="800px" @close="resetForm">
      <el-form :model="agentForm" :rules="formRules" ref="agentForm" label-width="120px">
        <!-- 头像区域 -->
        <el-form-item label="智能体头像">
          <div class="avatar-upload-section">
            <div class="avatar-preview">
              <el-upload class="avatar-uploader" :action="`${config.baseUrl}user/api.userinfo/uploadImg`"
                :show-file-list="false" name="img" :on-success="handleAvatarSuccess"
                :before-upload="beforeAvatarUpload">
                <div class="relative">
                  <img :src="agentForm.agentAvatar || defaultAvatar" class="avatar-image" />
                  <div class="uploading flex-center" v-show="uploading">
                    <i class="spin el-icon-loading"></i>
                  </div>
                  <div class="btn-edit" title="点击上传头像">
                    <i class="el-icon-upload2"></i>
                  </div>
                </div>
              </el-upload>
            </div>
            <div class="avatar-actions">
              <el-button size="small" @click="generateAvatar" :loading="generating">
                {{ generating ? 'AI生成中...' : 'AI生成头像' }}
              </el-button>
            </div>
          </div>
        </el-form-item>

        <!-- 基本信息 -->
        <el-form-item label="选择分类" prop="categoryGuid">
          <el-select v-model="agentForm.categoryGuid" placeholder="请选择分类" style="width: 100%">
            <el-option v-for="category in categoryList" :key="category.guid" :label="category.categoryName"
              :value="category.guid">
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="智能体名称" prop="agentName">
          <el-input v-model="agentForm.agentName" placeholder="请输入智能体名称" maxlength="50" show-word-limit />
        </el-form-item>

        <el-form-item label="智能体描述" prop="agentDesc">
          <el-input v-model="agentForm.agentDesc" type="textarea" :rows="3" placeholder="请输入智能体描述" />
        </el-form-item>

        <!-- 智能体类型 -->
        <el-form-item label="智能体类型">
          <el-radio-group v-model="agentForm.agentType">
            <el-radio v-for="type in agentTypes" :key="type.value" :label="type.value">
              {{ type.label }}
            </el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 角色设定提示词 - 仅内部类型显示 -->
        <el-form-item label="角色设定提示词" prop="promptContent" v-if="agentForm.agentType === 1">
          <el-input v-model="agentForm.promptContent" type="textarea" :rows="5" placeholder="请输入角色设定提示词" />
        </el-form-item>

        <!-- AI 模型 - 仅内部类型显示 -->
        <el-form-item label="AI 模型" v-if="agentForm.agentType === 1">
          <div class="picker-trigger" @click="openAiModelPicker">
            <span class="picker-text" :class="{ 'placeholder': !agentForm.aiModelGuid }">
              {{ agentForm.aiModelGuid ? currentAiModelLabel : '请选择 AI 模型（可选，默认使用平台模型）' }}
            </span>
            <i v-if="agentForm.aiModelGuid" class="el-icon-close picker-clear" @click.stop="clearAiModel"></i>
            <i v-else class="el-icon-arrow-right picker-arrow"></i>
          </div>
          <div class="form-tip">不同模型每次回复扣除的聊天点数不同，选择前请确认点数成本</div>
        </el-form-item>

        <!-- MCP 服务器 - 仅内部类型显示 -->
        <el-form-item label="MCP 服务器" v-if="agentForm.agentType === 1">
          <div class="picker-trigger" @click="openMcpPicker">
            <span class="picker-text" :class="{ 'placeholder': !agentForm.mcpServerGuid }">
              {{ agentForm.mcpServerGuid ? currentMcpServerName : '请选择 MCP 服务器（可选）' }}
            </span>
            <i class="el-icon-arrow-right picker-arrow"></i>
          </div>
          <div class="form-tip">选择要绑定的 MCP 服务器，不选表示不启用 MCP 工具</div>
        </el-form-item>

        <!-- MCP 参数配置 - 选中 MCP 且模板非空时显示 -->
        <el-form-item
          label="MCP 参数配置"
          v-if="agentForm.agentType === 1 && agentForm.mcpServerGuid && agentForm.mcpConfigParam.length > 0">
          <div class="mcp-param-list">
            <div class="mcp-param-item" v-for="(param, idx) in agentForm.mcpConfigParam" :key="param.field">
              <div class="mcp-sub-label">{{ param.field_name || param.field }}</div>
              <el-input
                v-model="agentForm.mcpConfigParam[idx].value"
                :placeholder="param.desc || ('请输入' + (param.field_name || param.field))" />
            </div>
          </div>
        </el-form-item>

        <!-- 商品搜索触发描述 - 选中 MCP 时显示 -->
        <el-form-item
          label="商品搜索触发描述"
          v-if="agentForm.agentType === 1 && agentForm.mcpServerGuid">
          <el-input
            v-model="agentForm.toolDesc"
            type="textarea"
            :rows="3"
            :placeholder="DEFAULT_TOOL_DESC"
            maxlength="500"
            show-word-limit />
          <div class="form-tip">控制 AI 在何种情况下触发商品搜索，留空则使用默认描述</div>
        </el-form-item>

        <!-- 密钥读取类型 - 非内部类型显示 -->
        <el-form-item label="密钥读取类型" v-if="agentForm.agentType !== 1">
          <el-radio-group v-model="agentForm.agentConfig.secret_key_type">
            <el-radio v-for="type in secretTypes" :key="type.value" :label="type.value">
              {{ type.label }}
            </el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 扣子智能体ID - 仅Coze类型显示 -->
        <el-form-item label="扣子智能体ID" v-if="agentForm.agentType === 3">
          <el-input v-model="agentForm.agentConfig.coze_sign" placeholder="请输入扣子智能体ID" />
        </el-form-item>

        <!-- 智能体秘钥 - 非内部类型显示 -->
        <el-form-item label="智能体秘钥" v-if="agentForm.agentType !== 1">
          <el-input v-model="agentForm.agentConfig.secret_token" placeholder="请输入智能体秘钥" type="password" show-password />
        </el-form-item>

        <!-- Dify自定义部署地址 - 仅Dify类型显示 -->
        <el-form-item label="Dify部署地址" v-if="agentForm.agentType === 2">
          <el-input v-model="agentForm.agentConfig.deploy_address" placeholder="请输入Dify自定义部署地址" />
        </el-form-item>

        <!-- 开场白 -->
        <el-form-item label="开场白">
          <el-input v-model="agentForm.welcomeMessage" placeholder="请输入开场白" />
        </el-form-item>

        <!-- 引导问题列表 -->
        <el-form-item label="引导问题">
          <div class="question-list">
            <el-input v-for="(question, index) in agentForm.commonQuestions" :key="index"
              v-model="agentForm.commonQuestions[index]" :placeholder="`请输入引导问题${index + 1}`"
              style="margin-bottom: 8px;" />
          </div>
        </el-form-item>

        <!-- 智能体类型（免费/付费） -->
        <el-form-item label="智能体类型">
          <el-radio-group v-model="agentForm.isPaid">
            <el-radio v-for="type in visibilityTypes" :key="type.value" :label="type.value">
              {{ type.label }}
            </el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 收费金额 - 仅付费类型显示 -->
        <el-form-item label="收费金额" prop="price" v-if="agentForm.isPaid === 1">
          <el-input v-model="agentForm.price" placeholder="请输入收费金额" type="number" step="0.01">
            <template slot="append">元</template>
          </el-input>
        </el-form-item>

        <!-- 试用聊天次数 - 仅付费类型显示 -->
        <el-form-item label="试用聊天次数" v-if="agentForm.isPaid === 1">
          <el-input v-model="agentForm.trial_chat_count" placeholder="请输入试用聊天次数" type="number">
            <template slot="append">次</template>
          </el-input>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="showDialog = false">取消</el-button>
        <el-button type="primary" @click="saveAgent" :loading="saving">
          {{ editingAgent ? '保存' : '创建' }}
        </el-button>
      </div>
    </el-dialog>

    <!-- MCP 服务器选择子弹窗 -->
    <el-dialog
      title="选择 MCP 服务器"
      :visible.sync="showMcpModal"
      width="520px"
      :append-to-body="true"
      custom-class="picker-sub-dialog">
      <div class="picker-list" v-loading="mcpLoading">
        <!-- "不启用" 选项始终在最上 -->
        <div class="picker-item" @click="pickTempMcp('')">
          <div class="radio" :class="{ 'checked': !tempMcpGuid }">
            <div class="radio-inner" v-if="!tempMcpGuid"></div>
          </div>
          <div class="picker-item-info">
            <div class="picker-item-name">不启用 MCP</div>
          </div>
        </div>
        <div
          v-for="server in mcpServerList"
          :key="server.guid"
          class="picker-item"
          @click="pickTempMcp(server.guid)">
          <div class="radio" :class="{ 'checked': tempMcpGuid === server.guid }">
            <div class="radio-inner" v-if="tempMcpGuid === server.guid"></div>
          </div>
          <div class="picker-item-info">
            <div class="picker-item-name">{{ server.serverName }}</div>
            <div class="picker-item-meta">
              {{ server.serverType }} · {{ (server.toolDefinitions || []).length }} 个工具
            </div>
          </div>
        </div>
        <div v-if="mcpServerList.length === 0 && !mcpLoading" class="picker-empty">
          该商户下暂无可用 MCP 服务器
        </div>
      </div>
      <div slot="footer">
        <el-button @click="closeMcpPicker">取消</el-button>
        <el-button type="primary" @click="confirmMcpPicker">确认</el-button>
      </div>
    </el-dialog>

    <!-- AI 模型选择子弹窗 -->
    <el-dialog
      title="选择 AI 模型"
      :visible.sync="showAiModelModal"
      width="520px"
      :append-to-body="true"
      custom-class="picker-sub-dialog">
      <div class="ai-vendor-tabs">
        <div
          v-for="vendor in aiVendors"
          :key="vendor.sign"
          class="ai-vendor-tab"
          :class="{ active: currentVendorSign === vendor.sign }"
          @click="switchVendor(vendor.sign)">
          {{ vendor.showName || vendor.show_name || vendor.name || vendor.sign }}
        </div>
      </div>
      <div class="picker-list ai-model-list" v-loading="aiModelLoading">
        <div class="picker-item" @click="pickTempAiModel('')">
          <div class="radio" :class="{ 'checked': !tempAiModelGuid }">
            <div class="radio-inner" v-if="!tempAiModelGuid"></div>
          </div>
          <div class="picker-item-info">
            <div class="picker-item-name">使用默认模型</div>
            <div class="picker-item-meta">不指定则使用系统默认模型</div>
          </div>
        </div>
        <div
          v-for="model in aiVendorModels"
          :key="model.guid"
          class="picker-item"
          @click="pickTempAiModel(model.guid)">
          <div class="radio" :class="{ 'checked': tempAiModelGuid === model.guid }">
            <div class="radio-inner" v-if="tempAiModelGuid === model.guid"></div>
          </div>
          <div class="picker-item-info">
            <div class="picker-item-name-row">
              <span class="picker-item-name">{{ model.modelName || model.modelCode }}</span>
              <span class="picker-item-price">每次 {{ model.usePrice || 0 }} 点</span>
            </div>
            <div
              class="picker-item-meta"
              v-if="model.modelCode && model.modelCode !== model.modelName">
              {{ model.modelCode }}
            </div>
          </div>
        </div>
        <div v-if="aiVendorModels.length === 0 && !aiModelLoading" class="picker-empty">
          该厂商下暂无可用模型
        </div>
      </div>
      <div slot="footer">
        <el-button @click="closeAiModelPicker">取消</el-button>
        <el-button type="primary" @click="confirmAiModelPicker">确认</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getMyAgentListApi,
  deleteAgentApi,
  updateMyAgentApi,
  createAgentApi,
  getCategoryListApi,
  generateAvatarApi,
  getMyEarningsApi,
  generateMiniCodeApi,
  platformRulesApi,
  getUserVipInfoApi,
  getVipPackageListApi,
  withdrawalListApi,
  transactionHistoryApi,
  createVipOrderApi,
  queryVipOrderApi,
  getMyDetailApi,
  getAgentMcpServerListApi,
  getAiVendorListApi,
  getAiVendorModelListApi,
} from '@/apis/agent'
import { paidAgentTextsApi } from '@/apis/user'
import config from '@/apis/config'

// 仅内部智能体（agentType=1）用到的 MCP / AI 模型相关常量
const TOOL_NAME = 'ycyz_shop_product_search'
const DEFAULT_TOOL_DESC = '分析用户的意图，判断用户是否有购买商品、课程、其他产品的意愿，如果有，尽可能的全面的把意图拆分成多个搜索关键字，多个关键字用英文分号;进行分割，关键字尽量简单，最多保留最关键的5个关键字，适合用于数据库搜索'

// 兼容 OpenAI function-calling 两种格式：{function:{name,description}} 或扁平 {name,description}
const getToolName = (tool) => (tool && tool.function && tool.function.name) || (tool && tool.name) || '未命名工具'
const getToolDescription = (tool) => {
  const d = (tool && tool.function && tool.function.description) != null
    ? tool.function.description
    : (tool && tool.description)
  return d == null ? '' : d
}
const setToolDescription = (tool, desc) => {
  if (tool && tool.function) tool.function.description = desc
  else if (tool) tool.description = desc
}

export default {
  name: 'my-agents',

  data() {
    return {
      config,
      loading: false,
      saving: false,
      generating: false, // AI生成头像状态
      uploading: false, // 头像上传状态
      showDialog: false,
      editingAgent: null,
      myAgentsList: [],
      defaultAvatar: 'https://ai-v2.deepcity.cn/default/head.png',

      // Tab相关
      tabs: ['我的智能体', '共创收益', '会员列表', '财务流水', '提现记录'],
      activeTab: 0,

      // 共创收益相关
      rulesList: [
        { name: '推广规则', icon: 'el-icon-promotion' },
        { name: '创作规则', icon: 'el-icon-edit' },
        { name: '平台规则', icon: 'el-icon-platform-eleme' }
      ],
      incomeData: {
        availableAmountText: '¥0.00',
        totalEarningsText: '¥0.00',
        totalWithdrawnText: '¥0.00'
      },

      // 会员相关
      vipInfo: {
        hasMembership: false,
        remainingDays: 0,
        userPoints: {
          totalPointsText: '0'
        }
      },
      vipPlans: [],
      selectedPlan: 0,

      // 订阅相关
      subscribeLoading: false,
      currentVipOrderNo: '',
      vipPollTimer: null,

      // 弹窗相关
      showMiniCodeModal: false,
      miniCodeUrl: '',
      showRuleModal: false,
      currentRuleTitle: '',
      currentRuleContent: '',

      // 财务流水相关
      financeLoading: false,
      summaryData: {
        totalIncome: '0.00',
        totalExpense: '0.00',
        commissionIncome: '0.00',
        withdrawExpense: '0.00'
      },
      financeRecordList: [],
      financePagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1
      },

      // 提现记录相关
      withdrawalLoading: false,
      withdrawalRecordList: [],
      withdrawalPagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1
      },

      // 智能体类型映射
      agentTypes: [
        { value: 1, label: '内部智能体' },
        { value: 2, label: 'Dify智能体' },
        { value: 3, label: 'Coze智能体' },
        // { value: 4, label: '阿里云百炼' }
      ],

      // 审核状态映射
      auditStatus: {
        1: '待审核',
        2: '审核通过',
        3: '审核拒绝'
      },

      // 分类相关
      categoryList: [],
      selectedCategory: null,

      // 秘钥类型
      secretTypes: [
        { value: 'default', label: '自定义输入' },
        { value: 'user', label: '读取用户保存密钥' }
      ],

      // 可见性类型选项
      visibilityTypes: [
        { value: 0, label: '免费' },
        { value: 1, label: '付费' }
      ],

      agentForm: {
        merchantGuid: '',
        agentName: '',
        agentType: 1, // 默认内部类型
        categoryGuid: '',
        promptContent: '',
        agentDesc: '',
        agentAvatar: '',
        isPaid: 0, // 默认免费
        price: '',
        trial_chat_count: 0, // 试用聊天次数
        isPublic: 1, // 默认公开
        agentConfig: {
          deploy_address: '',
          coze_sign: '',
          secret_token: '',
          secret_key_type: 'default'
        },
        knowledgeBaseIds: [],
        welcomeMessage: '',
        commonQuestions: ['', '', ''], // 默认3个问题
        // ↓↓ 仅内部智能体（agentType=1）使用的字段，提交时从 agentConfig 拆出
        mcpServerGuid: '',
        customToolDefinitions: [],
        mcpConfigParam: [],
        toolDesc: '',
        aiModelGuid: ''
      },

      // 智能体已保存的 MCP 参数值（编辑态缓存，待 MCP 列表加载后回填到模板）
      agentStoredMcpValues: [],

      // MCP 选择相关
      mcpServerList: [],
      mcpLoading: false,
      showMcpModal: false,
      tempMcpGuid: '',

      // AI 模型选择相关
      aiVendors: [],
      aiVendorModels: [],
      currentVendorSign: '',
      showAiModelModal: false,
      tempAiModelGuid: '',
      aiModelLoading: false,
      aiModelDetailCache: {},

      // 默认工具触发描述（template 占位符使用）
      DEFAULT_TOOL_DESC,

      formRules: {
        agentName: [
          { required: true, message: '请输入智能体名称', trigger: 'blur' },
          { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        agentDesc: [
          { required: true, message: '请输入描述', trigger: 'blur' },
        ],
        promptContent: [
          { required: false, message: '请输入系统提示词', trigger: 'blur' },
        ],
        categoryGuid: [
          { required: true, message: '请选择分类', trigger: 'change' }
        ],
        price: [
          { required: false, message: '请输入价格', trigger: 'blur' },
          { pattern: /^\d+(\.\d{1,2})?$/, message: '请输入正确的价格格式', trigger: 'blur' }
        ]
      },
      shareText: {
        noMemberTip: "", //付费智能体未开通会员提示
        noMemberSlogan: "", //付费智能体未开通会员slogo
        hasMemberTip: "", //付费智能体已开通会员后提示
        hasMemberSlogan: "", //付费智能体已开通会员slogo
        subscribeNotice: "", //付费智能体订阅须知文案
        miniprogramShareText: "", //付费智能体小程序分享文案
        promotionRules: "" //付费智能体推广规则文案
      }
    }
  },

  async mounted() {
    await this.loadMyAgents()
    await this.loadCategoryList()
    // 初始化其他Tab的数据
    await this.getMyEarnings()
    await this.getVipInfo()
    await this.getVipPackageList()
    await this.loadFinanceData()
    await this.loadWithdrawalData()
    await this.get_paid_agent_texts()
  },

  beforeDestroy() {
    // 清理VIP轮询定时器
    this.stopVipPolling()
  },

  computed: {
    // 获取商户GUID
    merchantGuid() {
      return this.$store.state.merchantGuid || 'e108201b02ae42e686bcc4c302cbbd11'
    },

    // 当前选中的 MCP 服务器
    currentMcpServer() {
      return this.mcpServerList.find(s => s.guid === this.agentForm.mcpServerGuid)
    },
    currentMcpServerName() {
      const srv = this.currentMcpServer
      return srv ? `${srv.serverName}（${srv.serverType}）` : ''
    },

    // 当前选中的 AI 模型 label
    currentAiModelLabel() {
      if (!this.agentForm.aiModelGuid) return ''
      const info = this.aiModelDetailCache[this.agentForm.aiModelGuid]
      if (!info) return '已选择模型'
      return `${info.modelName}${info.companyName ? '（' + info.companyName + '）' : ''}`
    }
  },

  methods: {
    async loadMyAgents() {
      this.loading = true
      try {
        // 调用真实API获取我的智能体列表
        const res = await getMyAgentListApi({
          merchantGuid: this.merchantGuid
        })

        if (res && res.code === 0 && res.data) {
          // 处理API返回的数据
          this.myAgentsList = res.data.data.map(agent => ({
            id: agent.guid,
            guid: agent.guid,
            name: agent.agentName,
            description: agent.agentDesc,
            systemPrompt: agent.promptContent,
            avatar: agent.agentAvatar || this.defaultAvatar,
            status: agent.auditStatus === 2 ? 'published' : 'draft',
            usageCount: agent.viewCount || 0,
            subscribeCount: agent.subscribeCount || 0,
            createTime: agent.createTime,
            updateTime: agent.updateTime,
            // 保留原始数据
            originalData: agent,
            // 智能体类型
            agentType: agent.agentType,
            agentTypeText: agent.agentTypeText,
            // 价格信息
            price: agent.price,
            priceText: agent.priceText,
            isPaid: agent.isPaid,
            isPaidText: agent.isPaidText,
            // 状态信息
            auditStatus: agent.auditStatus,
            auditStatusText: agent.auditStatusText,
            statusText: agent.statusText,
            isPublic: agent.isPublic,
            isPublicText: agent.isPublicText,
            // 分类信息
            category: agent.category,
            categoryGuid: agent.categoryGuid
          }))

          console.log('我的智能体列表加载成功:', this.myAgentsList)
        } else {
          this.$message.error(res.msg || '获取智能体列表失败')
        }
      } catch (error) {
        console.error('加载我的智能体失败:', error)
        this.$message.error('加载失败')
      } finally {
        this.loading = false
      }
    },

    // 加载分类列表
    async loadCategoryList() {
      try {
        const res = await getCategoryListApi({
          merchantGuid: this.merchantGuid
        })
        if (res && res.code === 0) {
          this.categoryList = res.data || []
        }
      } catch (error) {
        console.error('获取分类列表失败:', error)
      }
    },

    // AI生成头像
    async generateAvatar() {
      if (!this.agentForm.agentName.trim()) {
        this.$message.warning('请先输入智能体名称')
        return
      }
      if (!this.agentForm.agentDesc.trim()) {
        this.$message.warning('请先输入智能体描述')
        return
      }
      if (this.generating) return

      try {
        this.generating = true
        const res = await generateAvatarApi({
          merchantGuid: this.merchantGuid,
          agentName: this.agentForm.agentName.trim(),
          agentDesc: this.agentForm.agentDesc.trim()
        })

        if (res && res.code === 0) {
          this.agentForm.agentAvatar = res.data.data.imageUrl
          this.$message.success('头像生成成功')
        } else {
          this.$message.error(res.msg || '头像生成失败')
        }
      } catch (error) {
        console.error('AI生成头像失败:', error)
        this.$message.error('头像生成失败')
      } finally {
        this.generating = false
      }
    },

    // 头像上传成功回调
    handleAvatarSuccess({ code, data }) {
      if (code === 0) {
        this.agentForm.agentAvatar = data
        this.$message.success('头像上传成功')
      } else {
        this.$message.error('头像上传失败')
      }
      this.uploading = false
    },

    // 头像上传前的处理
    beforeAvatarUpload(file) {
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/gif'
      const isLt2M = file.size / 1024 / 1024 < 2

      if (!isJPG) {
        this.$message.error('上传头像图片只能是 JPG/PNG/GIF 格式!')
        return false
      }
      if (!isLt2M) {
        this.$message.error('上传头像图片大小不能超过 2MB!')
        return false
      }

      this.uploading = true
      return true
    },

    showCreateDialog() {
      this.editingAgent = null
      this.resetForm()
      this.showDialog = true
      // 进入新建态默认 agentType=1（内部智能体），预拉 MCP / AI 厂商
      this.loadMcpServerListIfNeeded()
      this.loadAiVendorsIfNeeded()
    },

    // 仅在 MCP 列表为空时拉取（避免重复请求）
    async loadMcpServerListIfNeeded() {
      if (this.mcpServerList.length > 0) {
        if (this.agentForm.mcpServerGuid) this.hydrateMcpConfigParam()
        return
      }
      try {
        this.mcpLoading = true
        const res = await getAgentMcpServerListApi({ merchantGuid: this.merchantGuid })
        this.mcpServerList = Array.isArray(res && res.data) ? res.data : []
        if (this.agentForm.mcpServerGuid) this.hydrateMcpConfigParam()
      } catch (e) {
        console.error('获取 MCP 服务器列表失败:', e)
        this.mcpServerList = []
        this.$message.error('获取 MCP 服务器列表失败')
      } finally {
        this.mcpLoading = false
      }
    },

    // 仅在 AI 厂商列表为空时拉取（PC 端不做厂商白名单限制，全量展示）
    async loadAiVendorsIfNeeded() {
      if (this.aiVendors.length === 0) {
        try {
          const res = await getAiVendorListApi({ merchantGuid: this.merchantGuid })
          this.aiVendors = Array.isArray(res && res.data) ? res.data : []
          if (this.aiVendors.length > 0 && !this.currentVendorSign) {
            this.currentVendorSign = this.aiVendors[0].sign
          }
        } catch (e) {
          console.error('获取 AI 厂商列表失败:', e)
          this.aiVendors = []
        }
      }
      if (this.currentVendorSign && this.aiVendorModels.length === 0) {
        await this.loadAiVendorModels(this.currentVendorSign)
      }
    },

    async loadAiVendorModels(vendorSign) {
      if (!vendorSign) return
      try {
        this.aiModelLoading = true
        const res = await getAiVendorModelListApi({
          modelVendorSign: vendorSign,
          modelType: 'text'
        })
        this.aiVendorModels = Array.isArray(res && res.data) ? res.data : []
        for (const m of this.aiVendorModels) {
          if (m.guid) {
            this.$set(this.aiModelDetailCache, m.guid, {
              modelName: m.modelName || '',
              companyName: m.companyName || '',
              usePrice: m.usePrice || 0
            })
          }
        }
      } catch (e) {
        console.error('获取 AI 模型列表失败:', e)
        this.aiVendorModels = []
      } finally {
        this.aiModelLoading = false
      }
    },

    // 合并：服务器 mcpConfigParam 模板 + 智能体存储值（字段匹配时覆盖 value）
    hydrateMcpConfigParam() {
      const srv = this.mcpServerList.find(s => s.guid === this.agentForm.mcpServerGuid)
      const template = srv && Array.isArray(srv.mcpConfigParam) ? srv.mcpConfigParam : []
      if (template.length === 0) {
        this.agentForm.mcpConfigParam = []
        return
      }
      const valueMap = {}
      for (const p of (this.agentStoredMcpValues || [])) {
        if (p && p.field) valueMap[p.field] = p.value != null ? p.value : ''
      }
      this.agentForm.mcpConfigParam = template.map(t => ({
        field: t.field,
        field_name: t.field_name || t.fieldName || t.field,
        desc: t.desc || '',
        value: valueMap[t.field] != null ? valueMap[t.field] : (t.value != null ? t.value : '')
      }))
    },

    // 从 customToolDefinitions 中提取 ycyz_shop_product_search 工具的描述（编辑回显用）
    extractYcyzToolDesc(tools) {
      if (!Array.isArray(tools)) return ''
      const target = tools.find(t => getToolName(t) === TOOL_NAME)
      if (!target) return ''
      const d = getToolDescription(target)
      return d === DEFAULT_TOOL_DESC ? '' : d
    },
    // 将 toolDesc 注入到 customToolDefinitions 对应工具的 description 字段
    applyYcyzToolDescToTools(tools, desc) {
      if (!Array.isArray(tools) || tools.length === 0) return tools
      const finalDesc = (desc || '').trim() || DEFAULT_TOOL_DESC
      return tools.map(t => {
        if (getToolName(t) === TOOL_NAME) {
          const clone = JSON.parse(JSON.stringify(t))
          setToolDescription(clone, finalDesc)
          return clone
        }
        return t
      })
    },

    // —————— MCP 服务器选择弹窗 ——————
    openMcpPicker() {
      if (this.mcpServerList.length === 0) this.loadMcpServerListIfNeeded()
      this.tempMcpGuid = this.agentForm.mcpServerGuid || ''
      this.showMcpModal = true
    },
    closeMcpPicker() {
      this.showMcpModal = false
    },
    pickTempMcp(guid) {
      this.tempMcpGuid = guid || ''
    },
    confirmMcpPicker() {
      const newGuid = this.tempMcpGuid || ''
      if (newGuid === this.agentForm.mcpServerGuid) {
        this.showMcpModal = false
        return
      }
      this.agentForm.mcpServerGuid = newGuid
      const srv = this.mcpServerList.find(s => s.guid === newGuid)
      this.agentForm.customToolDefinitions = srv
        ? JSON.parse(JSON.stringify(srv.toolDefinitions || []))
        : []
      this.agentStoredMcpValues = []
      this.hydrateMcpConfigParam()
      this.agentForm.toolDesc = ''
      this.showMcpModal = false
    },

    // —————— AI 模型选择弹窗 ——————
    openAiModelPicker() {
      this.tempAiModelGuid = this.agentForm.aiModelGuid || ''
      this.loadAiVendorsIfNeeded()
      this.showAiModelModal = true
    },
    closeAiModelPicker() {
      this.showAiModelModal = false
    },
    switchVendor(sign) {
      if (this.currentVendorSign === sign) return
      this.currentVendorSign = sign
      this.loadAiVendorModels(sign)
    },
    pickTempAiModel(guid) {
      this.tempAiModelGuid = guid || ''
    },
    confirmAiModelPicker() {
      this.agentForm.aiModelGuid = this.tempAiModelGuid || ''
      this.showAiModelModal = false
    },
    clearAiModel() {
      this.agentForm.aiModelGuid = ''
    },

    // 处理智能体点击事件
    handleAgentClick(agent) {
      console.log('点击智能体:', agent)
      // 可以在这里添加跳转到详情页或其他逻辑
      this.$emit('select-agent', agent)
    },

    // 处理编辑智能体事件
    handleEditAgent(agent) {
      this.editAgent(agent)
    },

    // 处理删除智能体事件
    handleDeleteAgent(agent) {
      this.deleteAgent(agent)
    },

    async editAgent(agent) {
      this.editingAgent = agent
      this.showDialog = true
      this.$emit('edit-agent', agent)

      // 通过详情接口拉取完整数据（包含 agentConfig 内嵌的 MCP / AI 模型字段）
      const loading = this.$loading({ lock: true, text: '加载中...' })
      try {
        const res = await getMyDetailApi({ guid: agent.guid })

        if (res && res.code === 0 && res.data) {
          const d = res.data
          // 从 agentConfig 中拆出 MCP/AI 模型相关字段到表单根部
          const rawConfig = d.agentConfig || {}
          const {
            mcpServerGuid = '',
            customToolDefinitions = [],
            mcpConfigParam = [],
            aiModelGuid = '',
            ...restConfig
          } = rawConfig
          const customTools = Array.isArray(customToolDefinitions)
            ? JSON.parse(JSON.stringify(customToolDefinitions))
            : []

          this.agentForm = {
            merchantGuid: this.merchantGuid,
            agentName: d.agentName || '',
            agentType: d.agentType || 1,
            categoryGuid: d.categoryGuid || '',
            promptContent: d.promptContent || '',
            agentDesc: d.agentDesc || '',
            agentAvatar: d.agentAvatar || '',
            isPaid: d.isPaid || 0,
            price: d.price || '',
            trial_chat_count: d.trialChatCount || 0,
            isPublic: d.isPublic || 1,
            agentConfig: Object.keys(restConfig).length > 0
              ? restConfig
              : { secret_token: '', secret_key_type: 'default', deploy_address: '', coze_sign: '' },
            knowledgeBaseIds: d.knowledgeBaseIds || [],
            welcomeMessage: d.welcomeMessage || '',
            commonQuestions: (d.commonQuestions && d.commonQuestions.length > 0) ? d.commonQuestions : ['', '', ''],
            mcpServerGuid: mcpServerGuid || '',
            customToolDefinitions: customTools,
            mcpConfigParam: [],
            toolDesc: this.extractYcyzToolDesc(customTools),
            aiModelGuid: aiModelGuid || ''
          }
          this.agentStoredMcpValues = Array.isArray(mcpConfigParam) ? mcpConfigParam : []

          // 编辑内部智能体时拉 MCP 服务器 + AI 厂商列表
          if (this.agentForm.agentType === 1) {
            this.loadMcpServerListIfNeeded()
            this.loadAiVendorsIfNeeded()
          }
        } else {
          this.$message.error((res && res.msg) || '获取智能体详情失败')
        }
      } catch (error) {
        console.error('获取智能体详情失败:', error)
        this.$message.error('获取智能体详情失败')
      } finally {
        loading.close()
      }
    },

    testAgent(agent) {
      this.$emit('test-agent', agent)
      this.$message.success(`开始测试智能体: ${agent.name}`)
    },

    async togglePublish(agent) {
      const action = agent.status === 'published' ? '下线' : '发布'

      try {
        await this.$confirm(`确定要${action}智能体"${agent.name}"吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        // 模拟API调用
        agent.status = agent.status === 'published' ? 'draft' : 'published'
        this.$message.success(`${action}成功`)
      } catch (error) {
        // 用户取消操作
      }
    },

    async deleteAgent(agent) {
      try {
        await this.$confirm(`确定要删除智能体"${agent.name}"吗？此操作不可恢复。`, '警告', {
          confirmButtonText: '确定删除',
          cancelButtonText: '取消',
          type: 'error'
        })

        // 调用删除API
        const res = await deleteAgentApi({
          agentGuid: agent.guid,
          merchantGuid: this.merchantGuid
        })

        if (res && res.code === 0) {
          // 从列表中移除
          const index = this.myAgentsList.findIndex(item => item.id === agent.id)
          if (index > -1) {
            this.myAgentsList.splice(index, 1)
          }
          this.$message.success('删除成功')
        } else {
          this.$message.error(res.msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除智能体失败:', error)
          this.$message.error('删除失败')
        }
      }
    },

    async saveAgent() {
      try {
        await this.$refs.agentForm.validate()

        // 表单验证
        if (!this.agentForm.categoryGuid) {
          this.$message.warning('请选择分类')
          return
        }

        if (!this.agentForm.agentName.trim()) {
          this.$message.warning('请输入智能体名称')
          return
        }

        if (this.agentForm.isPaid === 1 && !this.agentForm.price) {
          this.$message.warning('付费智能体请输入价格')
          return
        }

        this.saving = true

        // 过滤空的引导问题
        const filteredQuestions = this.agentForm.commonQuestions.filter(q => q.trim())

        const submitData = {
          agentName: this.agentForm.agentName.trim(),
          agentType: this.agentForm.agentType,
          categoryGuid: this.agentForm.categoryGuid,
          promptContent: this.agentForm.promptContent.trim(),
          agentDesc: this.agentForm.agentDesc.trim(),
          agentAvatar: this.agentForm.agentAvatar,
          isPaid: this.agentForm.isPaid,
          price: this.agentForm.isPaid === 1 ? parseFloat(this.agentForm.price) : 0,
          isPublic: 1, // 暂时都设为公开
          trial_chat_count: this.agentForm.trial_chat_count || 0,
          agentConfig: this.agentForm.agentConfig,
          knowledgeBaseIds: this.agentForm.knowledgeBaseIds,
          welcomeMessage: this.agentForm.welcomeMessage.trim(),
          commonQuestions: filteredQuestions
        }

        // MCP / AI 模型字段仅对内部智能体（agentType=1）生效，避免覆盖其他类型
        if (this.agentForm.agentType === 1) {
          submitData.mcpServerGuid = this.agentForm.mcpServerGuid || ''
          submitData.customToolDefinitions = this.applyYcyzToolDescToTools(
            this.agentForm.customToolDefinitions || [],
            this.agentForm.toolDesc
          )
          submitData.mcpConfigParam = (this.agentForm.mcpConfigParam || []).map(p => ({
            field: p.field,
            value: p.value != null ? p.value : ''
          }))
          submitData.aiModelGuid = this.agentForm.aiModelGuid || ''
        }

        if (this.editingAgent) {
          // 编辑模式
          submitData.guid = this.editingAgent.guid
          const res = await updateMyAgentApi(submitData)

          if (res && res.code === 0) {
            this.$message.success('更新成功')
            // 重新加载列表
            await this.loadMyAgents()
            this.showDialog = false
          } else {
            this.$message.error(res.msg || '更新失败')
          }
        } else {
          // 创建模式
          submitData.merchantGuid = this.merchantGuid
          const res = await createAgentApi(submitData)

          if (res && res.code === 0) {
            this.$message.success('创建成功')
            // 重新加载列表
            await this.loadMyAgents()
            this.showDialog = false
          } else {
            this.$message.error(res.msg || '创建失败')
          }
        }

      } catch (error) {
        console.error('保存失败:', error)
        this.$message.error('保存失败')
      } finally {
        this.saving = false
      }
    },

    resetForm() {
      this.agentForm = {
        merchantGuid: this.merchantGuid,
        agentName: '',
        agentType: 1,
        categoryGuid: '',
        promptContent: '',
        agentDesc: '',
        agentAvatar: '',
        isPaid: 0,
        price: '',
        trial_chat_count: 0,
        isPublic: 1,
        agentConfig: {
          deploy_address: '',
          coze_sign: '',
          secret_token: '',
          secret_key_type: 'default'
        },
        knowledgeBaseIds: [],
        welcomeMessage: '',
        commonQuestions: ['', '', ''],
        mcpServerGuid: '',
        customToolDefinitions: [],
        mcpConfigParam: [],
        toolDesc: '',
        aiModelGuid: ''
      }
      this.agentStoredMcpValues = []
      this.editingAgent = null
      this.selectedCategory = null
      if (this.$refs.agentForm) {
        this.$refs.agentForm.clearValidate()
      }
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('zh-CN')
    },

    // 获取我的收益
    async getMyEarnings() {
      try {
        const res = await getMyEarningsApi({
          merchantGuid: this.merchantGuid
        })
        if (res && res.data) {
          this.incomeData = res.data
        }
      } catch (error) {
        console.error('获取收益数据失败:', error)
        this.$message.error('获取收益数据失败')
      }
    },

    // 获取会员信息
    async getVipInfo() {
      try {
        const res = await getUserVipInfoApi({
          merchantGuid: this.merchantGuid
        })
        if (res && res.data) {
          this.vipInfo = res.data
        }
      } catch (error) {
        console.error('获取会员信息失败:', error)
        this.$message.error('获取会员信息失败')
      }
    },

    // 获取会员套餐列表
    async getVipPackageList() {
      try {
        const res = await getVipPackageListApi({
          merchantGuid: this.merchantGuid
        })
        if (res && res.data) {
          this.vipPlans = res.data.packages || []
        }
      } catch (error) {
        console.error('获取会员套餐失败:', error)
        this.$message.error('获取会员套餐失败')
      }
    },

    // 处理规则点击
    async handleRuleClick(index) {
      const ruleTypes = ['promotion', 'creation', 'platform']
      const ruleTitles = ['推广规则', '创作规则', '平台规则']

      try {
        const res = await platformRulesApi({
          merchantGuid: this.merchantGuid,
          ruleType: ruleTypes[index]
        })

        if (res && res.data) {
          this.currentRuleTitle = ruleTitles[index]
          this.currentRuleContent = res.data.content || '暂无规则内容'
          this.showRuleModal = true
        }
      } catch (error) {
        console.error('获取规则失败:', error)
        this.$message.error('获取规则失败')
      }
    },

    // 处理提现
    handleWithdraw() {
      // 生成小程序码并显示弹窗
      this.generateMiniCode()
    },

    // 生成小程序码
    async generateMiniCode() {
      try {
        const res = await generateMiniCodeApi({
          merchantGuid: this.merchantGuid,
          page: 'pages/raw-record/index' // 提现记录页面
        })

        if (res && res.data && res.data.codeImg) {
          this.miniCodeUrl = res.data.codeImg
          this.showMiniCodeModal = true
        } else {
          this.$message.error('生成小程序码失败')
        }
      } catch (error) {
        console.error('生成小程序码失败:', error)
        this.$message.error('生成小程序码失败')
      }
    },

    // 处理提现记录
    handleWithdrawRecord() {
      // 切换到提现记录tab
      this.activeTab = 4
    },

    // 处理财务流水
    handleFinanceFlow() {
      // 切换到财务流水tab
      this.activeTab = 3
    },

    // 为特定页面生成小程序码
    async generateMiniCodeForPage(page) {
      try {
        const res = await generateMiniCodeApi({
          merchantGuid: this.merchantGuid,
          page: page
        })

        if (res && res.data && res.data.codeImg) {
          this.miniCodeUrl = res.data.codeImg
          this.showMiniCodeModal = true
        } else {
          this.$message.error('生成小程序码失败')
        }
      } catch (error) {
        console.error('生成小程序码失败:', error)
        this.$message.error('生成小程序码失败')
      }
    },

    // 选择套餐
    selectPlan(index) {
      this.selectedPlan = index
    },

    // 处理订阅
    async handleSubscribe() {
      if (this.subscribeLoading) return

      if (this.vipPlans.length === 0) {
        this.$message.error('暂无可用套餐')
        return
      }

      const currentPlan = this.vipPlans[this.selectedPlan]
      if (!currentPlan) {
        this.$message.error('请选择套餐')
        return
      }

      try {
        this.subscribeLoading = true

        const params = {
          merchantGuid: this.merchantGuid,
          packageGuid: currentPlan.guid,
          payEnv: 'alipay_pc'
        }

        console.log('创建VIP订单参数:', params)
        const response = await createVipOrderApi(params)
        console.log('创建VIP订单响应:', response)

        if (response.code === 0) {
          // 订单创建成功，处理支付流程
          const { orderNo, payInfo } = response.data
          this.currentVipOrderNo = orderNo

          // 对于支付宝PC端，需要处理支付表单
          if (payInfo) {
            // 这里根据实际返回的payInfo结构处理支付
            // 如果有payForm字段，则提交表单；如果是其他支付信息，则相应处理
            if (payInfo.payForm) {
              this.submitVipPayForm(payInfo.payForm)
            } else {
              // 处理其他类型的支付信息
              this.$message.info('正在处理支付...')
            }

            // 开始轮询订单状态
            this.startVipPollingOrderStatus()
          } else {
            this.$message.error('支付信息获取失败')
          }
        } else {
          this.$message.error(response.msg || 'VIP订单创建失败')
        }
      } catch (error) {
        console.error('VIP订阅失败:', error)
        this.$message.error('VIP订阅失败，请稍后重试')
      } finally {
        this.subscribeLoading = false
      }
    },

    // 提交VIP支付表单
    submitVipPayForm(payForm) {
      try {
        // 解码base64编码的支付表单
        const decodedForm = window.atob(payForm)

        // 创建临时div来解析HTML
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = decodedForm

        // 获取表单元素
        const form = tempDiv.querySelector('form')
        if (form) {
          // 设置表单目标为新窗口
          form.target = '_blank'

          // 将表单添加到页面并提交
          document.body.appendChild(form)
          form.submit()

          // 提交后移除表单
          document.body.removeChild(form)

          this.$message.info('正在跳转到支付页面...')
        } else {
          this.$message.error('支付表单解析失败')
        }
      } catch (error) {
        console.error('提交VIP支付表单失败:', error)
        this.$message.error('支付页面跳转失败')
      }
    },

    // 开始轮询VIP订单状态
    startVipPollingOrderStatus() {
      let pollCount = 0
      const maxPolls = 3 // 最多轮询3次

      this.vipPollTimer = setInterval(async () => {
        pollCount++
        console.log(`第${pollCount}次查询VIP订单状态, 订单号:`, this.currentVipOrderNo)

        try {
          const response = await queryVipOrderApi({
            orderNo: this.currentVipOrderNo
          })

          console.log('VIP订单状态查询响应:', response)

          if (response.code === 0) {
            const { isPaid } = response.data

            if (isPaid) {
              // 支付成功
              this.handleVipPaymentSuccess()
              return
            }
          }

          // 达到最大轮询次数，停止轮询
          if (pollCount >= maxPolls) {
            this.stopVipPolling()
            console.log('VIP订单状态轮询结束，未检测到支付成功')
          }
        } catch (error) {
          console.error('查询VIP订单状态失败:', error)
          if (pollCount >= maxPolls) {
            this.stopVipPolling()
          }
        }
      }, 3000) // 每3秒查询一次
    },

    // 停止VIP轮询
    stopVipPolling() {
      if (this.vipPollTimer) {
        clearInterval(this.vipPollTimer)
        this.vipPollTimer = null
      }
    },

    // 处理VIP支付成功
    handleVipPaymentSuccess() {
      this.stopVipPolling()
      this.$message.success('支付成功！您可以继续聊天了')

      // 清除当前订单号
      this.currentVipOrderNo = ''

      // 刷新VIP信息
      this.loadVipInfo()

      // 这里可以添加其他支付成功后的处理逻辑
    },

    // 加载财务流水数据
    async loadFinanceData(page = 1) {
      this.financeLoading = true
      try {
        const params = {
          merchantGuid: this.merchantGuid,
          page: page,
          pageSize: this.financePagination.per_page
        }

        const response = await transactionHistoryApi(params)

        if (response && response.code === 0 && response.data) {
          // 处理汇总数据
          if (response.data.summary) {
            this.summaryData = {
              totalIncome: response.data.summary.totalIncome,
              totalExpense: response.data.summary.totalExpense,
              commissionIncome: response.data.summary.commissionIncome,
              withdrawExpense: response.data.summary.withdrawExpense
            }
          }

          // 处理流水记录数据，映射到组件需要的格式
          this.financeRecordList = response.data.list.map(item => ({
            guid: item.guid,
            type: item.flowType === 1 ? 'income' : 'expense',
            typeText: item.businessTypeText,
            flowTypeText: item.flowTypeText,
            businessType: item.businessType,
            businessTypeText: item.businessTypeText,
            description: item.description,
            amount: item.amount,
            amountText: item.amountText,
            balance: '', // API没有返回余额字段，可以根据需要计算或隐藏
            createTime: item.createTime,
            orderNo: item.businessOrderNo
          }))

          this.financePagination = {
            current_page: response.data.page,
            per_page: response.data.pageSize,
            total: response.data.total,
            last_page: Math.ceil(response.data.total / response.data.pageSize)
          }
        } else {
          this.$message.error(response.msg || '获取财务流水失败')
        }
      } catch (error) {
        console.error('获取财务流水失败:', error)
        this.$message.error('获取财务流水失败')
      } finally {
        this.financeLoading = false
      }
    },

    // 加载提现记录数据
    async loadWithdrawalData(page = 1) {
      this.withdrawalLoading = true
      try {
        const params = {
          merchantGuid: this.merchantGuid,
          page: page,
          pageSize: this.withdrawalPagination.per_page
        }

        const response = await withdrawalListApi(params)

        if (response && response.code === 0 && response.data) {
          // 处理API返回的数据，映射到组件需要的格式
          this.withdrawalRecordList = response.data.list.map(item => ({
            guid: item.guid,
            withdrawOrderNo: item.withdrawOrderNo,
            status: item.status,
            statusText: item.statusText,
            amount: item.amount,
            actualAmount: item.actualAmount,
            amountText: item.amountText,
            withdrawalMethodText: item.withdrawalMethodText,
            accountInfo: item.accountInfo,
            withdrawName: item.withdrawName,
            bankName: item.bankName,
            createTime: item.applyTime,
            processTime: item.auditTime || item.payTime,
            remark: item.remark,
            withdrawFee: item.withdrawFee
          }))

          this.withdrawalPagination = {
            current_page: response.data.page,
            per_page: response.data.pageSize,
            total: response.data.total,
            last_page: Math.ceil(response.data.total / response.data.pageSize)
          }
        } else {
          this.$message.error(response.msg || '获取提现记录失败')
        }
      } catch (error) {
        console.error('获取提现记录失败:', error)
        this.$message.error('获取提现记录失败')
      } finally {
        this.withdrawalLoading = false
      }
    },

    // 刷新财务流水数据
    async refreshFinanceData() {
      await this.loadFinanceData(this.financePagination.current_page)
    },

    // 刷新提现记录数据
    async refreshWithdrawalData() {
      await this.loadWithdrawalData(this.withdrawalPagination.current_page)
    },

    // 财务流水分页处理
    async handleFinancePageChange(page) {
      await this.loadFinanceData(page)
    },

    // 提现记录分页处理
    async handleWithdrawalPageChange(page) {
      await this.loadWithdrawalData(page)
    },

    // 获取交易类型标签类型
    getTransactionTagType(type) {
      const typeMap = {
        'income': 'success',
        'expense': 'warning'
      }
      return typeMap[type] || 'info'
    },

    // 根据业务类型获取标签类型
    getBusinessTagType(businessType) {
      const businessTypeMap = {
        1: 'primary',    // 智能体购买
        2: 'warning',    // 佣金提现
        3: 'success',    // 其他收入
        4: 'info',       // 其他支出
        5: 'danger',     // 退款
        6: 'purple'      // 创作者订阅
      }
      return businessTypeMap[businessType] || 'info'
    },

    // 获取状态标签类型
    getStatusTagType(status) {
      const statusMap = {
        100: 'info',     // 待审核
        200: 'success',  // 审核通过
        300: 'danger',   // 审核拒绝
        400: 'warning'   // 其他状态
      }
      return statusMap[status] || 'info'
    },

    // 复制订单号
    copyOrderNo(orderNo) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(orderNo).then(() => {
          this.$message.success('订单号已复制到剪贴板')
        }).catch(() => {
          this.$message.error('复制失败')
        })
      } else {
        // 兼容旧浏览器
        const textArea = document.createElement('textarea')
        textArea.value = orderNo
        document.body.appendChild(textArea)
        textArea.select()
        try {
          document.execCommand('copy')
          this.$message.success('订单号已复制到剪贴板')
        } catch (err) {
          this.$message.error('复制失败')
        }
        document.body.removeChild(textArea)
      }
    },
    async get_paid_agent_texts() {
      let res = await paidAgentTextsApi({
        merchantGuid: this.merchantGuid,
      })
      this.shareText = res.data;
    }
  }
}
</script>

<style lang="scss" scoped>
.my-agents {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;

  // Tab 切换栏样式
  .tab-container {
    background: white;
    border-bottom: 1px solid #e8e8e8;

    .tab-bar {
      display: flex;
      padding: 0 16px;

      .tab {
        flex: 1;
        text-align: center;
        padding: 12px 0;
        position: relative;
        cursor: pointer;

        .tab-text {
          font-size: 14px;
          color: #999999;
          font-weight: 400;
          transition: all 0.3s ease;
        }

        &.active {
          .tab-text {
            color: #7848f1;
            font-weight: 500;
          }

          &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 50%;
            transform: translateX(-50%);
            width: 40px;
            height: 2px;
            background: #7848f1;
            border-radius: 1px;
          }
        }

        &:hover:not(.active) {
          .tab-text {
            color: #666;
          }
        }
      }
    }
  }

  // Tab 内容区样式
  .tab-content {
    flex: 1;
    overflow-y: auto;
  }

  .my-agents-tab {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .header-section {
    background: white;
    padding: 16px 20px;
    border-bottom: 1px solid #e8e8e8;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .title-area {
      h2 {
        margin: 0 0 2px 0;
        font-size: 18px;
        color: #333;
        font-weight: 600;
      }

      p {
        margin: 0;
        color: #666;
        font-size: 12px;
      }
    }

    .action-area {
      .el-button {
        height: 32px;
        padding: 0 16px;
        font-size: 13px;
      }
    }
  }

  .agents-content {
    flex: 1;
    padding: 16px 20px;
    overflow-y: auto;

    .empty-state {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      .empty-content {
        text-align: center;

        i {
          font-size: 48px;
          color: #ddd;
          margin-bottom: 12px;
        }

        h3 {
          margin: 0 0 6px 0;
          color: #333;
          font-size: 16px;
        }

        p {
          margin: 0 0 16px 0;
          color: #666;
          font-size: 13px;
        }

        .el-button {
          height: 32px;
          padding: 0 16px;
          font-size: 13px;
        }
      }
    }

    .agents-grid {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .agent-card {
      display: flex;
      align-items: center;
      background: white;
      border-radius: 8px;
      padding: 12px;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
      transition: all 0.3s ease;
      position: relative;
      border: 1px solid #f0f0f0;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
        border-color: #e0e0e0;
      }

      .agent-avatar {
        width: 48px;
        height: 48px;
        border-radius: 6px;
        object-fit: cover;
        margin-right: 12px;
        flex-shrink: 0;
      }

      .agent-info {
        flex: 1;
        cursor: pointer;

        .agent-title {
          font-size: 14px;
          font-weight: 600;
          color: #333;
          margin-bottom: 4px;
          line-height: 1.3;
        }

        .agent-desc {
          font-size: 12px;
          color: #666;
          line-height: 1.4;
          margin-bottom: 8px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .agent-tags {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;

          .tag {
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 10px;
            line-height: 1.2;

            &.primary {
              background-color: #e3f2fd;
              color: #1976d2;
              border: 1px solid #bbdefb;
            }
          }
        }
      }

      .operate-box {
        display: flex;
        gap: 8px;
        margin-left: 12px;

        .edit,
        .delete {
          padding: 4px 8px;
          border-radius: 3px;
          font-size: 11px;
          cursor: pointer;
          transition: all 0.2s ease;
          line-height: 1.2;
        }

        .edit {
          background-color: #f0f9ff;
          color: #0369a1;
          border: 1px solid #bae6fd;

          &:hover {
            background-color: #e0f2fe;
          }
        }

        .delete {
          background-color: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;

          &:hover {
            background-color: #fee2e2;
          }
        }
      }

      .status {
        position: absolute;
        top: 8px;
        right: 8px;
        padding: 2px 6px;
        border-radius: 3px;
        font-size: 10px;
        background-color: #fef3c7;
        color: #d97706;
        border: 1px solid #fed7aa;
        line-height: 1.2;

        &.success {
          background-color: #dcfce7;
          color: #16a34a;
          border: 1px solid #bbf7d0;
        }
      }
    }
  }
}

// 头像上传区域样式 - 优化为更紧凑的设计
.avatar-upload-section {
  display: flex;
  align-items: center;
  gap: 12px;

  .avatar-preview {
    .avatar-uploader {
      .el-upload {
        border: none;
        border-radius: 6px;
        cursor: pointer;
        position: relative;
        overflow: hidden;
      }

      .relative {
        position: relative;
        display: inline-block;
      }

      .avatar-image {
        width: 64px;
        height: 64px;
        border-radius: 6px;
        object-fit: cover;
        border: 2px solid #e0e0e0;
        display: block;
      }

      .uploading {
        position: absolute;
        top: 0;
        left: 0;
        width: 64px;
        height: 64px;
        border-radius: 6px;
        background: rgba(0, 0, 0, 0.6);
        color: white;
        font-size: 16px;
        display: flex;
        align-items: center;
        justify-content: center;

        .spin {
          animation: rotating 2s linear infinite;
        }
      }

      .btn-edit {
        position: absolute;
        bottom: -2px;
        right: -2px;
        width: 20px;
        height: 20px;
        background: #409eff;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 10px;
        cursor: pointer;

        &:hover {
          background: #66b1ff;
        }
      }
    }
  }

  .avatar-actions {
    display: flex;
    flex-direction: column;
    gap: 6px;

    .el-button {
      height: 28px;
      padding: 0 12px;
      font-size: 12px;
    }
  }
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

// 引导问题列表样式
.question-list {
  .el-input {
    margin-bottom: 8px;
  }
}

.dialog-footer {
  text-align: right;

  .el-button {
    height: 32px;
    padding: 0 16px;
    font-size: 13px;
  }
}

// 移动端响应式优化
@media (max-width: 768px) {
  .my-agents {
    .header-section {
      padding: 12px 16px;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;

      .title-area {
        h2 {
          font-size: 16px;
        }

        p {
          font-size: 11px;
        }
      }

      .action-area {
        align-self: stretch;

        .el-button {
          width: 100%;
          height: 36px;
        }
      }
    }

    .agents-content {
      padding: 12px 16px;

      .empty-state .empty-content {
        i {
          font-size: 40px;
        }

        h3 {
          font-size: 14px;
        }

        p {
          font-size: 12px;
        }
      }

      .agents-grid {
        gap: 8px;

        .agent-card {
          padding: 10px;
          flex-direction: column;
          align-items: flex-start;

          .agent-avatar {
            width: 40px;
            height: 40px;
            margin-right: 0;
            margin-bottom: 8px;
            align-self: center;
          }

          .agent-info {
            width: 100%;
            text-align: center;

            .agent-title {
              font-size: 13px;
            }

            .agent-desc {
              font-size: 11px;
              margin-bottom: 6px;
            }

            .agent-tags {
              justify-content: center;
              gap: 4px;

              .tag {
                font-size: 9px;
                padding: 1px 4px;
              }
            }
          }

          .operate-box {
            margin-left: 0;
            margin-top: 8px;
            align-self: center;
            gap: 6px;

            .edit,
            .delete {
              font-size: 10px;
              padding: 3px 6px;
            }
          }

          .status {
            top: 6px;
            right: 6px;
            font-size: 9px;
            padding: 1px 4px;
          }
        }
      }
    }
  }

  // 对话框在移动端的优化
  .el-dialog {
    width: 95% !important;
    margin-top: 5vh !important;

    .el-dialog__body {
      padding: 16px !important;

      .el-form {
        .el-form-item {
          margin-bottom: 16px;

          .el-form-item__label {
            font-size: 12px;
            line-height: 1.3;
          }

          .el-form-item__content {

            .el-input,
            .el-textarea,
            .el-select {
              font-size: 13px;
            }
          }
        }
      }

      .avatar-upload-section {
        flex-direction: column;
        align-items: center;
        gap: 8px;

        .avatar-preview .avatar-uploader {
          .avatar-image {
            width: 56px;
            height: 56px;
          }

          .uploading {
            width: 56px;
            height: 56px;
          }
        }
      }
    }

    .el-dialog__footer {
      padding: 12px 16px !important;

      .el-button {
        height: 36px;
        font-size: 13px;
      }
    }
  }
}

// 超小屏幕优化
@media (max-width: 480px) {
  .my-agents {
    .agents-content .agents-grid .agent-card {
      .agent-info {
        .agent-title {
          font-size: 12px;
        }

        .agent-desc {
          font-size: 10px;
        }
      }

      .operate-box {

        .edit,
        .delete {
          font-size: 9px;
          padding: 2px 4px;
        }
      }
    }
  }
}

// 共创收益Tab样式
.income-tab {
  padding: 16px;
  background: #f8f9fa;

  .income-content {
    max-width: 1000px;
    margin: 0 auto;

    .section-title {
      font-size: 16px;
      font-weight: 600;
      color: #333;
      margin-bottom: 12px;
    }

    .rules-section {
      background: white;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 16px;

      .rules-grid {
        display: flex;
        gap: 12px;
        justify-content: space-around;

        .rule-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 12px;
          background: #f8f9fa;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          flex: 1;

          &:hover {
            background: #e6f0ff;
            transform: translateY(-1px);
          }

          .rule-text {
            font-size: 12px;
            color: #666;
            font-weight: 500;
          }
        }
      }
    }

    .my-income-section {
      background: white;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 16px;

      .income-card {
        background: #f8f9fa;
        border-radius: 8px;
        padding: 16px;
        display: flex;
        align-items: center;
        justify-content: space-between;

        .income-amount {
          .amount-value {
            font-size: 24px;
            font-weight: 700;
            color: #333;
            display: block;
            margin-bottom: 4px;
          }

          .amount-unit {
            font-size: 12px;
            color: #666;
          }
        }

        .withdraw-btn {
          border-radius: 16px;
          padding: 8px 16px;
          font-size: 13px;
          height: 32px;
        }
      }
    }

    .overview-section {
      background: white;
      border-radius: 12px;
      padding: 24px;
      margin-bottom: 24px;

      .overview-grid {
        display: flex;
        gap: 24px;

        .overview-item {
          flex: 1;
          background: #f8f9fa;
          border-radius: 12px;
          padding: 24px;
          text-align: center;

          .overview-value {
            font-size: 24px;
            font-weight: 700;
            color: #333;
            display: block;
            margin-bottom: 8px;
          }

          .overview-label {
            font-size: 14px;
            color: #666;
          }
        }
      }
    }

    .flow-section {
      background: white;
      border-radius: 12px;
      padding: 24px;

      .flow-buttons {
        display: flex;
        gap: 24px;

        .flow-btn {
          flex: 1;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: #f8f9fa;
          border: 1px solid #e8e8e8;
          color: #666;

          &:hover {
            background: #e6f0ff;
            border-color: #7848f1;
            color: #7848f1;
          }

          i {
            font-size: 18px;
          }
        }
      }
    }
  }
}

// 会员列表Tab样式
.vip-list-tab {
  padding: 16px;
  background: #f8f9fa;

  .vip-content {
    margin: 0 auto;

    .vip-card {
      background: linear-gradient(135deg, #7848f1, #9c6bff);
      border-radius: 12px;
      padding: 16px;
      margin-bottom: 16px;
      color: white;

      .vip-info {
        margin-bottom: 12px;

        .vip-title {
          font-size: 16px;
          font-weight: 600;
          margin: 0 0 4px 0;
        }

        .vip-desc {
          font-size: 12px;
          opacity: 0.9;
          margin: 0;
        }
      }

      .vip-points {
        display: flex;
        align-items: center;
        gap: 6px;

        .points-label {
          font-size: 12px;
          opacity: 0.9;
        }

        .points-value {
          font-size: 16px;
          font-weight: 600;
        }
      }
    }

    .vip-plans {
      background: white;
      border-radius: 12px;
      padding: 24px;

      .section-title {
        font-size: 20px;
        font-weight: 600;
        color: #333;
        margin-bottom: 20px;
      }

      .plan-grid {
        display: flex;
        gap: 16px;
        margin-bottom: 24px;
        justify-content: center;

        .plan-card {
          background: white;
          border: 2px solid #e8e8e8;
          border-radius: 12px;
          padding: 20px;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          min-width: 150px;

          &.selected {
            border-color: #7848f1;
            background: #f8f6ff;
          }

          &:hover {
            border-color: #7848f1;
          }

          .plan-header {
            margin-bottom: 12px;

            .plan-name {
              font-size: 14px;
              font-weight: 600;
              color: #333;
            }
          }

          .plan-price {
            display: flex;
            align-items: baseline;
            justify-content: center;
            margin-bottom: 8px;

            .price-symbol {
              font-size: 14px;
              color: #7848f1;
              font-weight: 600;
            }

            .price-amount {
              font-size: 24px;
              font-weight: 700;
              color: #7848f1;
            }
          }

          .plan-duration {
            font-size: 12px;
            color: #666;
            margin-bottom: 8px;
          }

          .plan-desc {
            font-size: 12px;
            color: #999;
          }
        }
      }

      .subscribe-btn {
        width: 200px;
        height: 40px;
        border-radius: 20px;
        display: block;
        margin: 0 auto;
      }
    }
  }
}

// 弹窗样式
.mini-code-content {
  text-align: center;

  .mini-code-image {
    width: 200px;
    height: 200px;
    margin-bottom: 16px;
    border-radius: 8px;
  }

  .mini-code-tip {
    font-size: 14px;
    color: #666;
    margin: 0;
  }
}

.rule-content {
  .rule-text {
    font-size: 14px;
    line-height: 1.6;
    color: #333;
    white-space: pre-wrap;
  }
}

// 财务流水Tab样式
.finance-record-tab {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;

  .finance-record-page {
    height: 100%;
    display: flex;
    flex-direction: column;

    // 统计数据区域
    .summary-section {
      margin-bottom: 16px;
      padding: 16px 16px 0 16px;

      .el-row {
        margin: 0 -8px;

        .el-col {
          padding: 0 8px;
        }
      }

      .summary-card {
        border: none;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
        border-radius: 8px;
        transition: all 0.3s ease;

        &:hover {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
          transform: translateY(-1px);
        }

        ::v-deep .el-card__body {
          padding: 16px;
        }

        .summary-item {
          text-align: center;

          .summary-label {
            font-size: 12px;
            color: #666;
            margin-bottom: 8px;
            font-weight: 500;
          }

          .summary-value {
            font-size: 20px;
            font-weight: 600;
            line-height: 1.2;

            &.income {
              color: #52c41a;
            }

            &.expense {
              color: #ff4d4f;
            }
          }
        }
      }
    }

    // 记录区域
    .record-section {
      flex: 1;
      overflow: hidden;

      .el-card {
        height: 100%;
        border: none;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

        ::v-deep .el-card__header {
          background: #fff;
          border-bottom: 1px solid #f0f0f0;
          padding: 16px 20px;

          .clearfix {
            display: flex;
            justify-content: space-between;
            align-items: center;

            .section-title {
              font-size: 16px;
              font-weight: 600;
              color: #333;
              margin: 0;
            }

            .el-button {
              height: 28px;
              padding: 0 12px;
              font-size: 12px;
              border-radius: 4px;
            }
          }
        }

        ::v-deep .el-card__body {
          padding: 0;
          height: calc(100% - 57px);
          overflow-y: auto;
        }
      }

      .record-list {
        padding: 12px 20px;

        .record-item {
          background: #fff;
          border-radius: 8px;
          margin-bottom: 12px;
          border: 1px solid #f0f0f0;
          transition: all 0.3s ease;

          &:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
            transform: translateY(-1px);
            border-color: #e0e0e0;
          }

          &:last-child {
            margin-bottom: 0;
          }

          .record-content {
            padding: 16px;

            .record-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 12px;

              .transaction-info {
                display: flex;
                align-items: center;
                gap: 8px;
                flex: 1;

                .transaction-type {
                  .el-tag {
                    height: 22px;
                    line-height: 20px;
                    font-size: 11px;
                    border-radius: 11px;
                    padding: 0 8px;
                  }
                }

                .transaction-desc {
                  font-size: 14px;
                  color: #333;
                  font-weight: 500;
                }
              }

              .amount-info {
                text-align: right;

                .amount {
                  font-size: 16px;
                  font-weight: 600;
                  margin-bottom: 4px;
                  line-height: 1.2;

                  &.income {
                    color: #52c41a;
                  }

                  &.expense {
                    color: #ff4d4f;
                  }
                }

                .balance {
                  font-size: 12px;
                  color: #666;
                  line-height: 1.2;
                }

                .business-type {
                  font-size: 12px;
                  color: #999;
                  margin-top: 4px;
                  line-height: 1.2;
                }
              }
            }

            .record-footer {
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-size: 12px;
              color: #999;

              .time-info {
                flex: 1;
              }

              .order-info {
                text-align: right;

                span {
                  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
                  background: #f8f9fa;
                  padding: 1px 4px;
                  border-radius: 2px;
                  font-size: 11px;
                }
              }
            }
          }
        }

        // 空状态
        .empty-state {
          text-align: center;
          padding: 80px 20px;
          color: #999;

          i {
            font-size: 64px;
            margin-bottom: 16px;
            display: block;
            color: #d9d9d9;
          }

          p {
            font-size: 16px;
            margin: 0 0 8px 0;
            color: #666;
            font-weight: 500;
          }
        }

        // 分页
        .pagination-wrapper {
          display: flex;
          justify-content: center;
          padding: 24px 0;
          border-top: 1px solid #f0f0f0;
          margin-top: 24px;

          ::v-deep .el-pagination {
            .el-pager li {
              min-width: 28px;
              height: 28px;
              line-height: 28px;
              font-size: 12px;
            }

            .btn-prev,
            .btn-next {
              min-width: 28px;
              height: 28px;
              line-height: 28px;
            }
          }
        }
      }
    }
  }
}

// 提现记录Tab样式
.withdrawal-record-tab {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;

  .withdrawal-record-page {
    height: 100%;
    display: flex;
    flex-direction: column;

    // 记录区域
    .record-section {
      flex: 1;
      overflow: hidden;

      .el-card {
        height: 100%;
        border: none;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

        ::v-deep .el-card__header {
          background: #fff;
          border-bottom: 1px solid #f0f0f0;
          padding: 16px 20px;

          .clearfix {
            display: flex;
            justify-content: space-between;
            align-items: center;

            .section-title {
              font-size: 16px;
              font-weight: 600;
              color: #333;
              margin: 0;
            }

            .el-button {
              height: 28px;
              padding: 0 12px;
              font-size: 12px;
              border-radius: 4px;
            }
          }
        }

        ::v-deep .el-card__body {
          padding: 0;
          height: calc(100% - 57px);
          overflow-y: auto;
        }
      }

      .record-list {
        padding: 12px 20px;

        .record-item {
          background: #fff;
          border-radius: 8px;
          margin-bottom: 12px;
          border: 1px solid #f0f0f0;
          transition: all 0.3s ease;

          &:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
            transform: translateY(-1px);
            border-color: #e0e0e0;
          }

          &:last-child {
            margin-bottom: 0;
          }

          .record-content {
            padding: 16px;

            .record-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 12px;

              .withdrawal-info {
                flex: 1;

                .order-info {
                  display: flex;
                  align-items: center;
                  margin-bottom: 6px;

                  .order-label {
                    font-size: 12px;
                    color: #666;
                    margin-right: 6px;
                  }

                  .order-value {
                    font-size: 13px;
                    color: #333;
                    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
                    margin-right: 6px;
                    background: #f8f9fa;
                    padding: 2px 6px;
                    border-radius: 3px;
                    font-weight: 500;
                  }

                  .el-button {
                    height: 20px;
                    padding: 0 6px;
                    font-size: 11px;
                    border-radius: 3px;
                  }
                }

                .status-info {
                  display: flex;
                  align-items: center;

                  .el-tag {
                    height: 22px;
                    line-height: 20px;
                    font-size: 11px;
                    border-radius: 11px;
                    padding: 0 8px;
                  }
                }
              }

              .amount-info {
                text-align: right;

                .amount {
                  font-size: 18px;
                  font-weight: 600;
                  color: #333;
                  margin-bottom: 2px;
                  line-height: 1.2;
                }

                .actual-amount {
                  font-size: 11px;
                  color: #52c41a;
                  line-height: 1.2;
                }
              }
            }

            .record-body {
              .el-row {
                margin-bottom: 8px;

                &:last-child {
                  margin-bottom: 0;
                }
              }

              .info-item {
                display: flex;
                align-items: center;
                font-size: 12px;
                line-height: 1.4;

                .label {
                  color: #666;
                  margin-right: 6px;
                  min-width: 60px;
                  font-weight: 500;
                }

                .value {
                  color: #333;
                  flex: 1;
                  word-break: break-all;

                  &.fee {
                    color: #ff4d4f;
                  }
                }
              }
            }
          }
        }

        // 空状态
        .empty-state {
          text-align: center;
          padding: 80px 20px;
          color: #999;

          i {
            font-size: 64px;
            margin-bottom: 16px;
            display: block;
            color: #d9d9d9;
          }

          p {
            font-size: 16px;
            margin: 0 0 8px 0;
            color: #666;
            font-weight: 500;
          }

          span {
            font-size: 14px;
            color: #999;
          }
        }

        // 分页
        .pagination-wrapper {
          display: flex;
          justify-content: center;
          padding: 24px 0;
          border-top: 1px solid #f0f0f0;
          margin-top: 24px;

          ::v-deep .el-pagination {
            .el-pager li {
              min-width: 28px;
              height: 28px;
              line-height: 28px;
              font-size: 12px;
            }

            .btn-prev,
            .btn-next {
              min-width: 28px;
              height: 28px;
              line-height: 28px;
            }
          }
        }
      }
    }
  }

  // 响应式设计 - 财务流水和提现记录
  @media (max-width: 768px) {

    .finance-record-tab,
    .withdrawal-record-tab {

      .finance-record-page,
      .withdrawal-record-page {
        .summary-section {
          margin-bottom: 12px;

          .el-row {
            margin: 0 -6px;

            .el-col {
              padding: 0 6px;
              margin-bottom: 8px;
            }
          }

          .summary-card {
            ::v-deep .el-card__body {
              padding: 12px;
            }

            .summary-item {
              .summary-label {
                font-size: 11px;
                margin-bottom: 6px;
              }

              .summary-value {
                font-size: 16px;
              }
            }
          }
        }

        .record-section {
          .el-card {
            ::v-deep .el-card__header {
              padding: 12px 16px;

              .clearfix {
                .section-title {
                  font-size: 14px;
                }

                .el-button {
                  height: 24px;
                  padding: 0 8px;
                  font-size: 11px;
                }
              }
            }
          }

          .record-list {
            padding: 8px 16px;

            .record-item {
              margin-bottom: 8px;

              .record-content {
                padding: 12px;

                .record-header {
                  flex-direction: column;
                  align-items: flex-start;
                  margin-bottom: 8px;

                  .transaction-info,
                  .withdrawal-info {
                    width: 100%;
                    margin-bottom: 8px;

                    .order-info {
                      flex-wrap: wrap;
                      margin-bottom: 4px;

                      .order-value {
                        font-size: 12px;
                        padding: 1px 4px;
                      }

                      .el-button {
                        height: 18px;
                        padding: 0 4px;
                        font-size: 10px;
                      }
                    }

                    .el-tag {
                      height: 20px;
                      line-height: 18px;
                      font-size: 10px;
                      padding: 0 6px;
                    }

                    .transaction-desc {
                      font-size: 13px;
                    }
                  }

                  .amount-info {
                    text-align: left;
                    width: 100%;

                    .amount {
                      font-size: 14px;
                    }

                    .actual-amount,
                    .balance {
                      font-size: 10px;
                    }
                  }
                }

                .record-body {
                  .info-item {
                    font-size: 11px;
                    margin-bottom: 4px;

                    .label {
                      min-width: 50px;
                      font-size: 10px;
                    }
                  }
                }

                .record-footer {
                  flex-direction: column;
                  align-items: flex-start;
                  gap: 4px;
                  font-size: 11px;
                }
              }
            }

            .empty-state {
              padding: 60px 16px;

              i {
                font-size: 48px;
              }

              p {
                font-size: 14px;
              }

              span {
                font-size: 12px;
              }
            }

            .pagination-wrapper {
              padding: 16px 0;

              ::v-deep .el-pagination {
                .el-pager li {
                  min-width: 24px;
                  height: 24px;
                  line-height: 24px;
                  font-size: 11px;
                }

                .btn-prev,
                .btn-next {
                  min-width: 24px;
                  height: 24px;
                  line-height: 24px;
                }
              }
            }
          }
        }
      }
    }
  }

  // 超小屏幕优化
  @media (max-width: 480px) {

    .finance-record-tab,
    .withdrawal-record-tab {

      .finance-record-page,
      .withdrawal-record-page {
        .summary-section {
          .el-row {
            margin: 0 -4px;

            .el-col {
              padding: 0 4px;
            }
          }

          .summary-card {
            ::v-deep .el-card__body {
              padding: 10px;
            }

            .summary-item {
              .summary-label {
                font-size: 10px;
                margin-bottom: 4px;
              }

              .summary-value {
                font-size: 14px;
              }
            }
          }
        }

        .record-section {
          .record-list {
            padding: 6px 12px;

            .record-item {
              .record-content {
                padding: 10px;

                .record-header {

                  .transaction-info,
                  .withdrawal-info {
                    .order-info {
                      .order-value {
                        font-size: 11px;
                        max-width: 120px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                      }
                    }

                    .transaction-desc {
                      font-size: 12px;
                    }
                  }

                  .amount-info {
                    .amount {
                      font-size: 13px;
                    }
                  }
                }

                .record-body {
                  .info-item {
                    font-size: 10px;

                    .label {
                      min-width: 45px;
                    }

                    .value {
                      font-size: 10px;
                    }
                  }
                }

                .record-footer {
                  font-size: 10px;
                }
              }
            }
          }
        }
      }
    }
  }
}

/* ─────── 主 Dialog 内：MCP / AI 模型选择触发器 + 参数列表 ─────── */
.picker-trigger {
  height: 36px;
  padding: 0 12px;
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  background: #fff;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    border-color: #c0c4cc;
  }

  .picker-text {
    flex: 1;
    font-size: 14px;
    color: #303133;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.placeholder {
      color: #c0c4cc;
    }
  }

  .picker-arrow,
  .picker-clear {
    color: #c0c4cc;
    font-size: 14px;
    margin-left: 8px;
  }

  .picker-clear {
    cursor: pointer;
    padding: 4px;

    &:hover {
      color: #f56c6c;
    }
  }
}

.form-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
  margin-top: 6px;
}

.mcp-param-list {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .mcp-param-item {
    .mcp-sub-label {
      font-size: 13px;
      color: #606266;
      margin-bottom: 6px;
    }
  }
}
</style>

<!-- 子 Dialog 样式（append-to-body 后脱离当前 scope，所以独立 non-scoped 块） -->
<style lang="scss">
.picker-sub-dialog {
  .el-dialog__body {
    padding: 0;
  }

  .ai-vendor-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;

    .ai-vendor-tab {
      padding: 6px 14px;
      border-radius: 16px;
      background: #f4f6f9;
      font-size: 13px;
      color: #555;
      cursor: pointer;
      transition: all 0.2s;
      user-select: none;

      &:hover {
        background: #ecf0f4;
      }

      &.active {
        background: #7848f1;
        color: #fff;
      }
    }
  }

  .picker-list {
    max-height: 400px;
    min-height: 200px;
    overflow-y: auto;

    &.ai-model-list {
      max-height: 360px;
    }

    .picker-item {
      display: flex;
      align-items: center;
      padding: 12px 16px;
      border-bottom: 1px solid #f5f5f5;
      cursor: pointer;
      transition: background 0.15s;

      &:hover {
        background: #fafbfc;
      }

      &:last-child {
        border-bottom: none;
      }

      .radio {
        width: 16px;
        height: 16px;
        border: 1.5px solid #ccc;
        border-radius: 50%;
        margin-right: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        &.checked {
          border-color: #7848f1;
        }

        .radio-inner {
          width: 8px;
          height: 8px;
          background: #7848f1;
          border-radius: 50%;
        }
      }

      .picker-item-info {
        flex: 1;
        min-width: 0;

        .picker-item-name-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .picker-item-name {
          font-size: 14px;
          color: #303133;
          font-weight: 500;
        }

        .picker-item-price {
          font-size: 12px;
          color: #7848f1;
          font-weight: 500;
          margin-left: 12px;
          flex-shrink: 0;
        }

        .picker-item-meta {
          font-size: 12px;
          color: #909399;
          margin-top: 4px;
        }
      }
    }

    .picker-empty {
      padding: 60px 20px;
      text-align: center;
      font-size: 13px;
      color: #909399;
    }
  }
}
</style>
